import Type from 'src/Type';
import config from 'src/utils/config';
import Primitive from 'src/utils/Primitive';

import Node from '../Node';

import type Context from 'src/Context';
import type { Expression } from 'src/types';
import type { BooleanNodeT } from '../value/BooleanNode';

export interface UpdatePv2NodeT {
    $type: 'UpdatePv2Node';
    id: Expression;
    removeAttributes: Expression;
    removeAttributeKey: Expression;
    useCopy?: BooleanNodeT;
    data: Expression;
}

export default class UpdatePv2Node extends Node<UpdatePv2NodeT> {
    static TYPE = Type.Object(
        { key: 'id', label: 'Pv2._id', type: 'StringNode', suggestions: 'string' },
        { key: 'removeAttributes', label: 'Remove Attributes', type: 'EvaluationNode' },
        {
            key: 'removeAttributeKey',
            label: 'Key for Attribute Removal',
            type: 'StringNode',
            suggestions: 'string',
        },
        { key: 'useCopy', label: 'Use Copy', type: 'BooleanNode' },
        { key: 'data', label: 'Data', type: 'ObjectNode' },
    );

    static uiConfig = {
        ...config.presets.tasker,
        description:
            'Update an existing Pv2. Passed `attributes` will be **append** to the existing Pv2.attributes, use the `removeAttributes` option to help with that. Enable `Use Copy` to apply the update to a copy of the Pv2 (created if one does not already exist) so that parent/child attribute validation is relaxed until the copy is merged back',
    };

    static getDefault(): Expression {
        return {
            ...super.getDefault(),
            id: this.getDefaultFor('StringNode'),
            removeAttributes: {
                ...this.getDefaultFor('ArrayNode'),
                value: [this.getDefaultFor('StringNode')],
            },
            removeAttributeKey: this.getDefaultFor('StringNode'),
            useCopy: { ...this.getDefaultFor('BooleanNode'), value: false },
            data: {
                ...this.getDefaultFor('ObjectNode'),
                value: {
                    attributes: {
                        ...this.getDefaultFor('ArrayNode'),
                        value: [Primitive.evaluation('')],
                    },
                },
            },
        };
    }

    async execute(context: Context): Promise<void> {
        const id = await context.evaluateLog('id', this.expression.id);
        const removeAttributes = await context.evaluateLog(
            'removeAttributes',
            this.expression.removeAttributes,
        );
        const removeAttributeKey = await context.evaluateLog(
            'removeAttributeKey',
            this.expression.removeAttributeKey,
        );
        const useCopy = await context.evaluateLog('useCopy', this.expression.useCopy);
        const data = await context.evaluateLog('data', this.expression.data);

        if (data?.name) context.setLastStackEntryMeta(data.name);
        else if (id) context.setLastStackEntryMeta(id);

        if (!data || typeof data !== 'object') throw new Error('Pv2 must be an object');

        context.addTaskerAction({
            kind: 'updatePv2',
            pv2: id,
            removeAttributes,
            removeAttributeKey,
            useCopy,
            data,
        });
    }
}
